import isObject from 'lodash/isObject';
import memoize from 'lodash/memoize';

interface AbstractGetSyncPromiseOptions extends GetSyncPromiseOptions {
    onPromiseResolve: () => void;
    onPromiseReject: (error: Error) => void;
    onCall?: (input: any, output: SyncPromise<any>) => void;
}

export type SyncPromise<T> = [T, Error | undefined, boolean];

export interface GetSyncPromiseOptions {
    disableThrashingDetection?: boolean;
}

export type GetSyncPromise = <T, U = undefined>(
    input: T | Promise<T>,
    defaultOutputValue?: U,
    options?: GetSyncPromiseOptions
) => SyncPromise<T | U>;

const cache = new memoize.Cache();

// "getSyncPromise" is a utility that allows the value, errors, and loading state of a promise to be
// accessed synchronously. If the promise has been resolved, the resolved value is returned. If the
// promise is pending, a default value is returned.

// The abstract implementation is used in a React hook `useGetSyncPromise` and a test utility
// `testGetSyncPromise`.

// Example:
//    const promise = Promise.resolve('later');
//    getSyncPromise(promise, 'now') // ['now', undefined, false]
//    await promise;
//    getSyncPromise(promise, 'now') // ['later', undefined, true]

export default function abstractGetSyncPromise({
    onPromiseResolve,
    onPromiseReject,
    onCall,
    ...parentOptions
}: AbstractGetSyncPromiseOptions): GetSyncPromise {
    return <T, U = undefined>(
        input: T | Promise<T>,
        defaultOutputValue?: U,
        childOptions: GetSyncPromiseOptions = {}
    ) => {
        let output: SyncPromise<T>;
        const options = {...parentOptions, ...childOptions};

        if (isPromise(input)) {
            if (!cache.has(input)) {
                cache.set(input, [undefined, undefined, false]);
                if (!options.disableThrashingDetection) detectThrashing();

                input.then(
                    (t: T) => {
                        cache.set(input, [t, undefined, true]);
                        onPromiseResolve();
                    },
                    (error: Error) => {
                        cache.set(input, [undefined, error, true]);
                        onPromiseReject(error);
                        console.error(error);
                    }
                );
            }

            const [outputValue, error, isLoaded] = cache.get(input);
            output = [isLoaded ? outputValue : defaultOutputValue, error, isLoaded];
        } else {
            output = [input, undefined, true];
        }

        if (onCall) onCall(input, output);
        return output;
    };
}

function isPromise(input: any): input is Promise<any> {
    return isObject(input) && typeof (input as any).then === 'function';
}

let cacheCalls = 0;
let cacheSecond = 0;
let cacheWarning = false;
function detectThrashing() {
    if (DEPLOYMENT !== 'production') {
        const nextCacheSecond = Math.floor(Date.now() / 1000);
        if (cacheSecond !== nextCacheSecond) {
            if (cacheCalls > 30 && !cacheWarning) {
                // eslint-disable-next-line no-console
                console.trace(
                    'getSyncPromise appears to be thrashing. ' +
                        `It was called ${cacheCalls} times in the last second. ` +
                        'Check that promises are properly memoized'
                );
                cacheWarning = true;
            }
            cacheSecond = nextCacheSecond;
            cacheCalls = 0;
        }
        cacheCalls++;
    }
}
